// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/apiService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Check if user is already logged in on app start
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const savedUser = localStorage.getItem('user');
    
    console.log('Auth initialization:', { hasToken: !!token, hasUser: !!savedUser });
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      setIsAuthenticated(true);
      
      // Make sure API service has the token
      apiService.updateToken(token);
    }
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    try {
      setLoading(true);
      const response = await apiService.login({
        username: credentials.username,
        password: credentials.password
      });

      if (response.user) {
        const userData = {
          name: response.user.fullName || response.user.username,
          email: response.user.email || '',
          role: response.user.role || 'User',
          avatar: response.user.avatar || null
        };
        
        setUser(userData);
        setIsAuthenticated(true);
        
        // Debug: Verify token is saved
        console.log('Login successful, token saved:', !!response.accessToken);
        console.log('Token preview:', response.accessToken ? response.accessToken.substring(0, 20) + '...' : 'No token');
        
        return { success: true };
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Extract meaningful error message
      let errorMessage = 'Login failed. Please try again.';
      
      if (error.message) {
        if (error.message.includes('Invalid username or password') || 
            error.message.includes('password does not match') ||
            error.message.includes('authentication failed')) {
          errorMessage = 'Invalid username or password. Please check your credentials and try again.';
        } else if (error.message.includes('User not found') || 
                   error.message.includes('username not found')) {
          errorMessage = 'Username not found. Please check your username and try again.';
        } else if (error.message.includes('Account locked') || 
                   error.message.includes('account disabled')) {
          errorMessage = 'Your account has been locked or disabled. Please contact support.';
        } else if (error.message.includes('network') || 
                   error.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          // Use the backend error message if it's user-friendly
          errorMessage = error.message.length < 100 ? error.message : errorMessage;
        }
      }
      
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  // Update user data (for profile updates)
  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
    localStorage.setItem('user', JSON.stringify(updatedUserData));
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      user,
      login,
      logout,
      setUser,
      updateUser,
      loading
    }}>
      {children}
    </AuthContext.Provider>
  );
};